/**
 * POWDI „Savijautos testas" – konfigūracija (v1.2, 2026-10-06)
 * ------------------------------------------------------------------
 * Visa testo logika valdoma šiuo failu. Keičiant produktus, svorius
 * ar tekstus – redaguojamas TIK šis config'as, ne testo kodas.
 *
 * v1.2 pakeitimai (2026-10-06): TIK rezultatų puslapio tekstai ir
 *   rodymas – klausimai, taškai ir produktų matrica nepakeisti.
 *   Handle'ai suvienodinti su gyva svetaine. Žr. resultTexts, shortNames,
 *   heroWhy, altReason.
 *
 * Architektūra: taškų (scoring) sistema, ne sprendimų medis.
 *   K1–K2 kaupia taškus į 5 krypčių kaupiklius.
 *   K3–K4 nustato segmentą (naujokas/patyręs) ir formatą.
 *   K5 nustato apimtį (vienas produktas / rinkinys) – AOV svertas.
 *   Rezultatas = matrix[kryptis][segmentas][formatas],
 *   o jei scope=rinkinys ir krypčiai yra rinkinys – hero keičiamas juo.
 *
 * SVARBU (teisinė pusė):
 *   - Rezultatų tekstuose TIK „siūlome / tinka tavo kasdienai".
 *   - JOKIŲ formuluočių „tau trūksta X", „išspręs Y", „sustiprins Z".
 *   - Produktų kainos/nuotraukos/atsiliepimai traukiami realiu laiku
 *     per /products/{handle}.js – NIEKADA nekopijuojami į šį failą.
 */

const POWDI_QUIZ_CONFIG = {

  meta: {
    version: "1.2",
    title: "Savijautos testas",
    intro: "Atsakyk į 5 klausimus – parinksime, kas tinka tavo kasdienai.",
    directions: ["energija", "imunitetas", "zarnynas", "grozis", "balansas"],
    // Rezervuota ateičiai (baltymų launch'as): "sportas"
  },

  /* ================================================================
   * KLAUSIMAI
   * ================================================================ */
  questions: [

    {
      id: "q1",
      axis: "busena",
      text: "Kaip dažniausiai jautiesi darbo dienos viduryje?",
      answers: [
        { id: "q1a", text: "Pavargęs/-usi, trūksta energijos",
          scores: { energija: 2 } },
        { id: "q1b", text: "Normaliai, bet norėčiau jaustis stipriau",
          scores: { energija: 1, imunitetas: 1 } },
        { id: "q1c", text: "Jaučiu, kad virškinimas galėtų būti geresnis",
          scores: { zarnynas: 2 } },
        { id: "q1d", text: "Gerai – noriu palaikyti tai, ką turiu",
          scores: { balansas: 2 } }
      ]
    },

    {
      id: "q2",
      axis: "tikslas",           // Didžiausias svoris + tiebreaker
      text: "Kas tau šiuo metu svarbiausia?",
      answers: [
        { id: "q2a", text: "Daugiau energijos kasdienai",
          scores: { energija: 3 } },
        { id: "q2b", text: "Atsparumas ir imuniteto palaikymas",
          scores: { imunitetas: 3 } },
        { id: "q2c", text: "Virškinimas ir lengvumo jausmas",
          scores: { zarnynas: 3 } },
        { id: "q2d", text: "Oda, plaukai ir nagai",
          scores: { grozis: 3 } },
        { id: "q2e", text: "Ramesnė, stabilesnė kasdiena",
          scores: { balansas: 3 } }
      ]
    },

    {
      id: "q3",
      axis: "patirtis",
      text: "Ar jau esi vartojęs/-usi supermaistą ar papildus?",
      answers: [
        { id: "q3a", text: "Ne, būtų pirmas kartas",
          sets: { segment: "naujokas" } },
        { id: "q3b", text: "Bandžiau, bet nepritapo",
          sets: { segment: "naujokas" } },
        { id: "q3c", text: "Taip, vartoju reguliariai",
          sets: { segment: "patyres" } }
      ]
    },

    {
      id: "q4",
      axis: "formatas",
      text: "Kaip patogiausia vartoti?",
      answers: [
        { id: "q4a", text: "Tabletės – greita ir be skonio",
          sets: { format: "tabletes" } },
        { id: "q4b", text: "Milteliai – į kokteilį, jogurtą ar vandenį",
          sets: { format: "milteliai" } },
        { id: "q4c", text: "Nesvarbu – kaip patogiau",
          sets: { format: "bet_koks" } }
      ]
    },

    {
      id: "q5",
      axis: "apimtis",           // AOV svertas: vienas produktas vs rinkinys
      text: "Koks startas tau patrauklesnis?",
      answers: [
        { id: "q5a", text: "Vienas produktas – paprasta pradžia",
          sets: { scope: "vienas" } },
        { id: "q5b", text: "Paruoštas rinkinys – viskas viename",
          sets: { scope: "rinkinys" } }
      ]
    }
  ],

  /* ================================================================
   * REZULTATŲ MATRICA (vieno produkto kelias)
   * matrix[kryptis][segmentas][formatas] = { hero, alt: [2 handle'ai] }
   *
   * Taisyklės:
   *   - NAUJOKAS gauna MIX (paruoštas atsakymas be derinimo).
   *   - PATYRĘS gauna pavienius ingredientus pagal formatą.
   *   - "bet_koks" formatas paveldi geriausią krypties variantą.
   * ================================================================ */
  matrix: {

    energija: {
      naujokas: {
        tabletes:  { hero: "ekologiska-spirulina-tabletes",
                     alt: ["energy-mix-supermaisto-misinys", "spirulina-ir-chlorella-mix-tabletes"] },
        milteliai: { hero: "energy-mix-supermaisto-misinys",
                     alt: ["japoniska-matcha-arbata-milteliai", "ekologiska-spirulina-milteliai"] },
        bet_koks:  { hero: "energy-mix-supermaisto-misinys",
                     alt: ["ekologiska-spirulina-tabletes", "japoniska-matcha-arbata-milteliai"] }
      },
      patyres: {
        tabletes:  { hero: "ekologiska-spirulina-tabletes",
                     alt: ["havaju-spirulina-tabletes-fikocianinas", "spirulina-ir-chlorella-mix-tabletes"] },
        milteliai: { hero: "japoniska-matcha-arbata-milteliai",
                     alt: ["ekologiska-spirulina-milteliai", "energy-mix-supermaisto-misinys"] },
        bet_koks:  { hero: "ekologiska-spirulina-tabletes",
                     alt: ["japoniska-matcha-arbata-milteliai", "energy-mix-supermaisto-misinys"] }
      }
    },

    imunitetas: {
      naujokas: {
        tabletes:  { hero: "spirulina-ir-chlorella-mix-tabletes",
                     alt: ["immunity-mix-supermaisto-misinys", "acerola-vitaminas-c-milteliai"] },
        milteliai: { hero: "immunity-mix-supermaisto-misinys",
                     alt: ["acerola-vitaminas-c-milteliai", "ekologiska-spirulina-milteliai"] },
        bet_koks:  { hero: "immunity-mix-supermaisto-misinys",
                     alt: ["spirulina-ir-chlorella-mix-tabletes", "acerola-vitaminas-c-milteliai"] }
      },
      patyres: {
        tabletes:  { hero: "spirulina-ir-chlorella-mix-tabletes",
                     alt: ["ekologiska-chlorella-tabletes", "acerola-vitaminas-c-milteliai"] },
        milteliai: { hero: "acerola-vitaminas-c-milteliai",
                     alt: ["immunity-mix-supermaisto-misinys", "juodojo-berzo-grybas-chaga-milteliai"] },
        bet_koks:  { hero: "spirulina-ir-chlorella-mix-tabletes",
                     alt: ["acerola-vitaminas-c-milteliai", "immunity-mix-supermaisto-misinys"] }
      }
    },

    zarnynas: {
      naujokas: {
        tabletes:  { hero: "ekologiska-chlorella-tabletes",
                     alt: ["green-mix-supermaisto-misinys", "spirulina-ir-chlorella-mix-tabletes"] },
        milteliai: { hero: "green-mix-supermaisto-misinys",
                     alt: ["ekologiska-chlorella-milteliai", "kvieciu-zelmenu-milteliai"] },
        bet_koks:  { hero: "green-mix-supermaisto-misinys",
                     alt: ["ekologiska-chlorella-tabletes", "ekologiska-chlorella-milteliai"] }
      },
      patyres: {
        tabletes:  { hero: "ekologiska-chlorella-tabletes",
                     alt: ["spirulina-ir-chlorella-mix-tabletes", "green-mix-supermaisto-misinys"] },
        milteliai: { hero: "ekologiska-chlorella-milteliai",
                     alt: ["green-mix-supermaisto-misinys", "kvieciu-zelmenu-milteliai"] },
        bet_koks:  { hero: "ekologiska-chlorella-tabletes",
                     alt: ["green-mix-supermaisto-misinys", "ekologiska-chlorella-milteliai"] }
      }
    },

    grozis: {
      naujokas: {
        tabletes:  { hero: "spirulina-ir-chlorella-mix-tabletes",   // tablečių formos kolageno nėra
                     alt: ["jurinis-kolagenas-milteliai-300g", "acerola-vitaminas-c-milteliai"] },
        milteliai: { hero: "jurinis-kolagenas-milteliai-300g",  // jūrinis
                     alt: ["acerola-vitaminas-c-milteliai", "premium-gyvulinis-kolagenas-milteliai"] },
        bet_koks:  { hero: "jurinis-kolagenas-milteliai-300g",
                     alt: ["acerola-vitaminas-c-milteliai", "spirulina-ir-chlorella-mix-tabletes"] }
      },
      patyres: {
        tabletes:  { hero: "spirulina-ir-chlorella-mix-tabletes",
                     alt: ["jurinis-kolagenas-milteliai-300g", "havaju-spirulina-tabletes-fikocianinas"] },
        milteliai: { hero: "jurinis-kolagenas-milteliai-300g",
                     alt: ["premium-gyvulinis-kolagenas-milteliai", "acerola-vitaminas-c-milteliai"] },
        bet_koks:  { hero: "jurinis-kolagenas-milteliai-300g",
                     alt: ["acerola-vitaminas-c-milteliai", "premium-gyvulinis-kolagenas-milteliai"] }
      }
    },

    balansas: {
      naujokas: {
        tabletes:  { hero: "ekologiska-spirulina-tabletes",
                     alt: ["spirulina-ir-chlorella-mix-tabletes", "green-mix-supermaisto-misinys"] },
        milteliai: { hero: "green-mix-supermaisto-misinys",
                     alt: ["asvaganda-ashwagandha-milteliai", "ekologiska-spirulina-milteliai"] },
        bet_koks:  { hero: "green-mix-supermaisto-misinys",
                     alt: ["ekologiska-spirulina-tabletes", "asvaganda-ashwagandha-milteliai"] }
      },
      patyres: {
        tabletes:  { hero: "ekologiska-spirulina-tabletes",
                     alt: ["ekologiska-chlorella-tabletes", "spirulina-ir-chlorella-mix-tabletes"] },
        milteliai: { hero: "asvaganda-ashwagandha-milteliai",
                     alt: ["reishi-grybu-milteliai-tikrinis-blizgutis", "juodojo-berzo-grybas-chaga-milteliai"] },
        bet_koks:  { hero: "ekologiska-spirulina-tabletes",
                     alt: ["asvaganda-ashwagandha-milteliai", "reishi-grybu-milteliai-tikrinis-blizgutis"] }
      }
    }
  },

  /* ================================================================
   * RINKINIŲ MATRICA (kai K5 scope = "rinkinys")
   * Hero keičiamas krypties rinkiniu; alt lieka iš pagrindinės
   * matricos (dedupe – jei sutampa su rinkinio sudėtimi, praleisti).
   * Jei krypčiai rinkinio nėra (null) – naudojama pagrindinė matrica,
   * o rezultato tekste rodoma pastaba apie derinį (žr. resultTexts).
   * ================================================================ */
  bundleMatrix: {
    energija:   "rinkinys-gelezies-ir-baltymu-saltinis",      // Spirulina + Acerola
    imunitetas: "rinkinys-nenugalimas-imunitetas",
    zarnynas:   "rinkinys-chlorella-acerola",            // Chlorella + Acerola
    grozis:     null,                                          // rinkinio nėra – fallback
    balansas:   "rinkinys-green-mix-energy-mix-immunity-mix"  // Supergalios (3 MIX)
  },

  /* ================================================================
   * SPRENDIMO LOGIKA (pseudokodas programuotojui)
   * ================================================================
   * 1. score = { energija:0, imunitetas:0, zarnynas:0, grozis:0, balansas:0 }
   * 2. Kiekvienam atsakymui: score[k] += answers.scores[k]
   * 3. kryptis = max(score); LYGIŲJŲ atveju laimi ta kryptis,
   *    kurią pasirinko Q2 (tikslas visada persveria būseną).
   * 4. rezultatas = matrix[kryptis][segment][format]
   * 5. if (scope === "rinkinys" && bundleMatrix[kryptis])
   *        hero = bundleMatrix[kryptis];
   *        alt  = du pirmieji pagrindinės matricos produktai,
   *               kurie nesikartoja su rinkinio sudėtimi.
   * 5a. Jei hero (ypač rinkinys) išparduotas – naudoti pagrindinės
   *     matricos hero (vieną produktą), kad nerekomenduotume to,
   *     ko negalima nupirkti.
   * 6. Rezultato puslapis ir Omnisend – žr. resultTexts komentarą.
   * 7. Produktų duomenys: fetch(`/products/${handle}.js`)
   *    → pavadinimas, kaina, nuotrauka; atsiliepimai – Judge.me widget.
   * ================================================================ */

  /* ================================================================
   * REZULTATŲ PUSLAPIO TEKSTAI (v1.2)
   * SVARBU: tik „siūlome / tinka" kalba. Jokių „tau trūksta X",
   * jokių sveikatos pažadų. Leidžiami tik ES patvirtinti teiginiai
   * (pvz. vitaminas C, geležis) tiksliai tokia formuluote, kaip žemiau.
   *
   * Puslapio struktūra (iš viršaus į apačią):
   *   1. eyebrow                       „Tavo testo rezultatas"
   *   2. H1 = headlinePrefix + shortNames[hero]
   *                                    „Mūsų rekomendacija – Chlorellos tabletės"
   *   3. intro = introStart + introDirection[kryptis] + ", "
   *              + introFormat[format] + " ir " + introThird + " " + introEnd
   *      introThird: scope==="rinkinys" ? introScope.rinkinys
   *                                     : introSegment[segment]
   *   4. Hero kortelė: pavadinimas, žvaigždutės, „Kodėl siūlome" blokas
   *      (heroWhyLabel + heroWhy[hero]), dydžio pasirinkimas
   *      (PAGAL NUTYLĖJIMĄ – PIRMASIS / MAŽIAUSIAS PRIEINAMAS variantas),
   *      vienkartinis / prenumerata, „Į krepšelį".
   *   5. El. pašto blokas – lieka TOJE PAČIOJE vietoje (tarp hero ir alt).
   *   6. altHeading („Taip pat galėtų tikti") + altIntro; kiekvienoje alt kortelėje vietoj aprašymo
   *      rodoma altReason[handle] (1 eilutė), kaina ir „Į krepšelį".
   *   7. Nuorodos: retakeLabel (iš naujo paleidžia testą) ir ctaAll.
   *
   * Omnisend: rezultato antraštė el. laiške = shortNames[hero]
   *   (pvz. „Chlorellos tabletės", BE „Mūsų rekomendacija –").
   *   Krypties reikšmė (directionLabels) siunčiama atskirai, jei flow
   *   ją naudoja sąlygoms / segmentams.
   * ================================================================ */
  resultTexts: {
    eyebrow:        "Tavo testo rezultatas",
    headlinePrefix: "Mūsų rekomendacija – ",

    introStart: "Pagal tavo atsakymus, tu ",
    introDirection: {
      energija:   "nori daugiau energijos kasdien",
      imunitetas: "nori kasdien rūpintis imunitetu",
      zarnynas:   "nori daugiau lengvumo kasdien",
      grozis:     "nori pasirūpinti oda, plaukais ir nagais",
      balansas:   "nori ramesnio, stabilesnio dienos ritmo"
    },
    introFormat: {
      tabletes:  "renkiesi tabletes",
      milteliai: "renkiesi miltelius",
      bet_koks:  "forma tau nesvarbi"
    },
    introSegment: {
      naujokas: "pradedi nuo vieno produkto",
      patyres:  "supermaistą jau pažįsti"
    },
    introScope: {
      rinkinys: "nori paruošto rinkinio"
    },
    introEnd: ". Štai nuo ko siūlytume pradėti.",
    // Pvz.: „Pagal tavo atsakymus, tu nori daugiau lengvumo kasdien, renkiesi
    //        tabletes ir pradedi nuo vieno produkto. Štai nuo ko siūlytume pradėti."

    heroWhyLabel: "Kodėl siūlome",

    altHeading: "Taip pat galėtų tikti",
    altIntro:   "Atrinkome pagal tavo atsakymus ir mūsų patirtį – rinkis tai, kas tau patogiau.",

    bundleFallback: "Šiai krypčiai rinkinio dar neturime – siūlome derinį iš dviejų produktų.",
    ctaHero:     "Į krepšelį",          // atidaro cart drawer su progreso juosta
    ctaAlt:      "Į krepšelį",
    ctaAll:      "Visi produktai →",
    retakeLabel: "Pakartoti testą",
    emailOffer:  "Atsiųsime tavo rekomendaciją el. paštu",
    emailNote:   "receptai, patarimai ir pasiūlymai – atsisakysi bet kada",

    // Vidinės krypčių reikšmės (analitikai / Omnisend sąlygoms) – vartotojui puslapyje NERODOMOS
    directionLabels: {
      energija:   "Energijos kryptis",
      imunitetas: "Imuniteto palaikymo kryptis",
      zarnynas:   "Virškinimo kryptis",
      grozis:     "Odos, plaukų ir nagų kryptis",
      balansas:   "Balanso kryptis"
    }
  },

  /* ================================================================
   * TRUMPI PAVADINIMAI (max 2 žodžiai)
   * Naudojami H1 („Mūsų rekomendacija – …") ir Omnisend laiško antraštei.
   * ================================================================ */
  shortNames: {
    "ekologiska-spirulina-tabletes":             "Spirulinos tabletės",
    "ekologiska-spirulina-milteliai":            "Spirulinos milteliai",
    "havaju-spirulina-tabletes-fikocianinas":    "Havajų spirulina",
    "ekologiska-chlorella-tabletes":             "Chlorellos tabletės",
    "ekologiska-chlorella-milteliai":            "Chlorellos milteliai",
    "spirulina-ir-chlorella-mix-tabletes":       "Spirulina + chlorella",
    "energy-mix-supermaisto-misinys":            "Energy Mix",
    "green-mix-supermaisto-misinys":             "Green Mix",
    "immunity-mix-supermaisto-misinys":          "Immunity Mix",
    "japoniska-matcha-arbata-milteliai":         "Matcha arbata",
    "acerola-vitaminas-c-milteliai":             "Acerolos milteliai",
    "kvieciu-zelmenu-milteliai":                 "Kviečių želmenys",
    "jurinis-kolagenas-milteliai-300g":          "Jūrinis kolagenas",
    "premium-gyvulinis-kolagenas-milteliai":     "Kolageno peptidai",
    "asvaganda-ashwagandha-milteliai":           "Ašvagandos milteliai",
    "reishi-grybu-milteliai-tikrinis-blizgutis": "Reishi grybai",
    "juodojo-berzo-grybas-chaga-milteliai":      "Čagos milteliai",
    "rinkinys-gelezies-ir-baltymu-saltinis":     "Spirulina + acerola",
    "rinkinys-nenugalimas-imunitetas":           "Imuniteto rinkinys",
    "rinkinys-chlorella-acerola":                "Chlorella + acerola",
    "rinkinys-green-mix-energy-mix-immunity-mix":"Trigubas kokteilis"
  },

  /* ================================================================
   * „KODĖL SIŪLOME" – po hero produkto pavadinimu (1-2 sakiniai)
   * Raktas = hero handle (įskaitant rinkinius).
   * ================================================================ */
  heroWhy: {
    "ekologiska-spirulina-tabletes":
      "Populiariausias mūsų produktas – ekologiška spirulina be rišiklių ir priedų. Spirulinoje gausu geležies, o geležis padeda mažinti pavargimo jausmą ir nuovargį.",
    "ekologiska-chlorella-tabletes":
      "Mikrodumbliai be jokių priedų, kuriuos lengva įtraukti į dieną – kelios tabletės su maistu, be skonio ir maišymo.",
    "ekologiska-chlorella-milteliai":
      "Ekologiška chlorella be priedų – patogu įmaišyti į kokteilį, sultis ar jogurtą.",
    "spirulina-ir-chlorella-mix-tabletes":
      "Du populiariausi mikrodumbliai vienoje tabletėje – nereikia rinktis tarp spirulinos ir chlorellos.",
    "energy-mix-supermaisto-misinys":
      "Paruoštas ekologiškas mišinys rytui – įmaišai į kokteilį ar jogurtą, nieko derinti nereikia.",
    "green-mix-supermaisto-misinys":
      "10 žaliųjų ingredientų viename ekologiškame mišinyje – įmaišai į rytinį kokteilį, nieko derinti nereikia.",
    "immunity-mix-supermaisto-misinys":
      "Paruoštas ekologiškas mišinys su cinamonu ir grybais – įmaišai į šiltą gėrimą, nieko derinti nereikia.",
    "japoniska-matcha-arbata-milteliai":
      "Ceremoninė ekologiška matcha – švelnesnė alternatyva kavai rytui ar popietei.",
    "acerola-vitaminas-c-milteliai":
      "Natūralus vitamino C šaltinis iš acerolos vaisių. Vitaminas C padeda palaikyti normalią imuninės sistemos veiklą.",
    "jurinis-kolagenas-milteliai-300g":
      "Hidrolizuotas jūrinis kolagenas be skonio – greitai ištirpsta kavoje, vandenyje ar kokteilyje. Derink su acerola: vitaminas C padeda palaikyti normalų kolageno susidarymą, kad būtų užtikrinta normali odos funkcija.",
    "asvaganda-ashwagandha-milteliai":
      "Ekologiška ašvaganda – tradicinis ajurvedos augalas, kurį lengva įmaišyti į šiltą augalinį gėrimą.",
    // Rinkiniai (kai K5 = „Paruoštas rinkinys")
    "rinkinys-gelezies-ir-baltymu-saltinis":
      "Spirulina ir acerola viename rinkinyje – vitaminas C padidina geležies įsisavinimą.",
    "rinkinys-nenugalimas-imunitetas":
      "Acerola, spirulina ir Immunity Mix viename rinkinyje – vitaminas C padeda palaikyti normalią imuninės sistemos veiklą.",
    "rinkinys-chlorella-acerola":
      "Chlorella ir acerola viename rinkinyje – acerolos rūgštumas atsveria žolinį chlorellos skonį.",
    "rinkinys-green-mix-energy-mix-immunity-mix":
      "Visi trys Mix viename rinkinyje – kiekvieną dieną gali rinktis pagal nuotaiką."
  },

  /* ================================================================
   * ALT KORTELĖS – 1 eilutė vietoj produkto aprašymo
   * Raktas = alt handle.
   * ================================================================ */
  altReason: {
    "ekologiska-spirulina-tabletes":             "Jei nori paprasčiausio starto – populiariausias mūsų produktas tabletėmis.",
    "ekologiska-spirulina-milteliai":            "Jei mėgsti kokteilius – tą pačią spiruliną gali įmaišyti į gėrimą.",
    "havaju-spirulina-tabletes-fikocianinas":    "Jei nori išbandyti kitokią spiruliną – užaugintą Havajuose.",
    "ekologiska-chlorella-tabletes":             "Jei nori vieno ingrediento tabletėmis – ekologiška chlorella be priedų.",
    "ekologiska-chlorella-milteliai":            "Jei nori chlorellos miltelių pavidalu – į kokteilį ar sultis.",
    "spirulina-ir-chlorella-mix-tabletes":       "Jei nori abiejų mikrodumblių vienoje tabletėje.",
    "energy-mix-supermaisto-misinys":            "Jei mieliau geri nei ryji tabletes – paruoštas ekologiškas mišinys rytui.",
    "green-mix-supermaisto-misinys":             "Jei mieliau geri nei ryji tabletes – 10 žaliųjų ingredientų viename rytiniame kokteilyje.",
    "immunity-mix-supermaisto-misinys":          "Jei mieliau geri nei ryji tabletes – ekologiškas mišinys su cinamonu ir grybais šiltam gėrimui.",
    "japoniska-matcha-arbata-milteliai":         "Jei ieškai švelnesnės alternatyvos kavai.",
    "acerola-vitaminas-c-milteliai":             "Jei nori pridėti natūralaus vitamino C iš acerolos vaisių.",
    "kvieciu-zelmenu-milteliai":                 "Jei mėgsti žalius kokteilius – švelnaus, žolinio skonio.",
    "jurinis-kolagenas-milteliai-300g":          "Jei nori kolageno, kurio beveik nejausi gėrime.",
    "premium-gyvulinis-kolagenas-milteliai":     "Jei nori I ir III tipo kolageno – neutralaus skonio, greitai ištirpsta kavoje ar kokteilyje.",
    "asvaganda-ashwagandha-milteliai":           "Jei mėgsti šiltą gėrimą vakare – ašvaganda tinka į augalinį pieną.",
    "reishi-grybu-milteliai-tikrinis-blizgutis": "Jei mėgsti šiltus gėrimus – reishi tinka į kakavą ar latte.",
    "juodojo-berzo-grybas-chaga-milteliai":      "Jei mėgsti šiltus gėrimus – čaga tinka į kavą ar kakavą."
  },

  /* ================================================================
   * ANALITIKA (GA4 / Shopify events)
   * ================================================================ */
  analytics: {
    events: {
      start:      "quiz_start",        // param: entry_point (menu|banner|chip)
      answer:     "quiz_answer",       // param: question_id, answer_id
      result:     "quiz_result",       // param: direction, segment, format, scope
      addToCart:  "quiz_add_to_cart",  // param: handle, position (hero|alt)
      emailSubmit:"quiz_email_submit"
    },
    entryPoints: ["menu", "banner", "chip_nezinau"]
  }
};

window.POWDI_QUIZ_CONFIG = POWDI_QUIZ_CONFIG;
